import { Badge, Drawer, Empty, List, Space, Tabs, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchDrawMessages } from '@/services/oms/message';

const { Text } = Typography;

type MessageItem = {
  id?: number;
  kyid?: number;
  Message?: string;
  app?: string;
  app_act?: string;
  PDate?: string;
};

type MessageDrawerProps = {
  open: boolean;
  onClose: () => void;
  onPendingCountChange?: (count: number) => void;
};

const normalizePayload = (resp: any) => {
  if (resp && typeof resp === 'object' && 'data' in resp) {
    return resp.data as Record<string, any>;
  }
  return resp ?? {};
};

const MessageDrawer = ({
  open,
  onClose,
  onPendingCountChange,
}: MessageDrawerProps) => {
  const [activeStatus, setActiveStatus] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<MessageItem[]>([]);
  const [pageInfo, setPageInfo] = useState<Record<string, any>>({});
  const [num, setNum] = useState<any[]>([]);

  const pendingCount = useMemo(() => num?.[0]?.[0]?.[0] ?? 0, [num]);
  const completedCount = useMemo(() => num?.[1]?.[0]?.[0] ?? 0, [num]);

  const loadList = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const resp = await fetchDrawMessages({ status: activeStatus, page });
      const payload = normalizePayload(resp);
      setList(payload?.list ?? []);
      setPageInfo(payload?.pageinfo ?? {});
      setNum(payload?.num ?? []);
      if (typeof onPendingCountChange === 'function') {
        const nextCount = payload?.num?.[0]?.[0]?.[0] ?? 0;
        onPendingCountChange(nextCount);
      }
    } finally {
      setLoading(false);
    }
  }, [activeStatus, onPendingCountChange, open, page]);

  useEffect(() => {
    if (open) {
      loadList();
    }
  }, [loadList, open]);

  useEffect(() => {
    if (!open) {
      setPage(1);
    }
  }, [open]);

  const tabItems = [
    {
      key: '0',
      label: (
        <Space size={6}>
          <span>待办任务</span>
          <Badge count={pendingCount} showZero color="#155DFB" size="small" />
        </Space>
      ),
    },
    {
      key: '1',
      label: (
        <Space size={6}>
          <span>已完成的任务</span>
          <Badge count={completedCount} showZero color="#52C41A" size="small" />
        </Space>
      ),
    },
  ];

  const pageSize = Number(pageInfo?.pagesize || 6) || 6;
  const total = Number(pageInfo?.total || 0) || 0;

  return (
    <Drawer
      title="消息提醒"
      placement="right"
      width={700}
      open={open}
      onClose={onClose}
    >
      <Tabs
        activeKey={String(activeStatus)}
        items={tabItems}
        onChange={(key) => {
          setActiveStatus(Number(key));
          setPage(1);
        }}
      />
      <List
        loading={loading}
        dataSource={list}
        locale={{ emptyText: <Empty description="暂无消息" /> }}
        pagination={
          total > 0
            ? {
                current: page,
                pageSize,
                total,
                onChange: (nextPage) => setPage(nextPage),
                showSizeChanger: false,
              }
            : false
        }
        renderItem={(item) => (
          <List.Item>
            <Space orientation="vertical" size={2} style={{ width: '100%' }}>
              <Text>{item?.Message || '-'}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item?.PDate || '-'}
              </Text>
            </Space>
          </List.Item>
        )}
      />
    </Drawer>
  );
};

export default MessageDrawer;
